/**
 * TabPanels is a container for a list of TabPanel elements
 */
import React, { ReactNode, cloneElement, ReactElement, Children, Component } from "react";
import classNames from "classnames";
import { TabsContext } from "./Tabs";
import { TransitionGroup, Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import { AnimationDuration } from "../../../styles/Animations";
import forceReflow from "../../../utils/forceReflow";

interface ITabPanelsProps {
  children?: ReactNode;
  className?: string;
  /**
   * Transition between heights for each TabPanel. Optional due to the
   * slight performance overhead of height transitions causing a reflow.
   * Only use this when not transitioning height looks super jank
   */
  transitionHeight?: boolean;
}

class TabPanels extends Component<ITabPanelsProps> {
  public static contextType = TabsContext;

  // Get the direction to transition towards based on the current/previous selectedIndex
  private getDirection = (child: any): -1 | 1 => {
    const { selectedIndex, prevSelectedIndex } = this.context;
    if (selectedIndex > prevSelectedIndex) return child.props.in ? 1 : -1;
    return child.props.in ? -1 : 1;
  };

  public render(): ReactNode {
    const { children, className, transitionHeight } = this.props;
    const { selectedIndex, panelHeight } = this.context;
    const panelsHeight = transitionHeight && panelHeight !== 0 ? panelHeight : undefined;

    return (
      <div className={classNames("tabs__tab-panels", className)} style={{ height: panelsHeight }}>
        <TransitionGroup
          component={null}
          childFactory={(child): ReactElement =>
            cloneElement(child, {
              direction: this.getDirection(child),
            })
          }
        >
          {Children.toArray(children)
            .filter(Boolean)
            .map(
              (child, index) =>
                index === selectedIndex && (
                  <Transition key={`tab-panel-${index}`} timeout={AnimationDuration.Short} onEnter={forceReflow}>
                    {(status: TransitionStatus, { direction }: { direction: -1 | 1 }): ReactNode =>
                      cloneElement(child as ReactElement, { index, status, direction })
                    }
                  </Transition>
                ),
            )}
        </TransitionGroup>
      </div>
    );
  }
}

export default TabPanels;
